import { ProductCreateDto, ProductSelectDto, ProductUpdateDto, ProductsSelectDto } from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type UserRoleEndpoint, useGetCurrentUser, useGetUserSelectedCompanyId } from './users.query';

const productSupportedRoles: UserRoleEndpoint[] = ['expert', 'innovator', 'intel'];

type UseGetProductsOptions = {
  enabled?: boolean;
};

export const useGetProducts = (technologyId?: string, options: UseGetProductsOptions = {}) => {
  const { enabled: isQueryEnabled = true } = options;
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { roleEndpoint } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  const hasAccess = Boolean(roleEndpoint && productSupportedRoles.includes(roleEndpoint));
  const requiresCompanyContext = roleEndpoint === 'innovator';
  const enabled = Boolean(
    isQueryEnabled && isAuthenticated && hasAccess && (!requiresCompanyContext || selectedCompanyId),
  );

  return useQuery<ProductSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!roleEndpoint || !productSupportedRoles.includes(roleEndpoint)) {
        throw new Error('User role not supported for product access');
      }

      const queryParams = technologyId ? `?technologyId=${technologyId}` : '';
      const result = await apiGet<ProductsSelectDto>({
        path: `/v1/${roleEndpoint}/products${queryParams}`,
      });

      return result.products;
    },
    queryKey: enabled ? queryKeys.product.getProducts(technologyId) : queryKeys.placeholders.default,
  });
};

export const useGetProduct = (productId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { roleEndpoint } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const hasAccess = Boolean(roleEndpoint && productSupportedRoles.includes(roleEndpoint));
  const requiresCompanyContext = roleEndpoint === 'innovator';
  const enabled = Boolean(isAuthenticated && hasAccess && productId && (!requiresCompanyContext || selectedCompanyId));

  return useQuery<ProductSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!roleEndpoint || !productSupportedRoles.includes(roleEndpoint)) {
        throw new Error('User role not supported for product access');
      }

      return await apiGet<ProductSelectDto>({
        path: `/v1/${roleEndpoint}/products/${productId}`,
      });
    },
    queryKey: enabled ? queryKeys.product.getProduct(productId!) : queryKeys.placeholders.default,
  });
};

export const useInnovatorCreateProduct = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();
  const { isInnovator } = useGetCurrentUser();

  const onSuccess = (data: ProductSelectDto) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.product.getProducts(),
    });
    queryClient.setQueryData(queryKeys.product.getProduct(data.id), data);
  };

  return useMutation<ProductSelectDto, Error, ProductCreateDto>({
    mutationFn: async (formData) => {
      if (!isInnovator) {
        throw new Error('Only innovators can create products');
      }

      return await apiPost<ProductSelectDto>({
        body: formData,
        path: '/v1/innovator/products',
      });
    },
    onSuccess,
  });
};

export const useInnovatorUpdateProduct = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();
  const { isInnovator } = useGetCurrentUser();

  const onSuccess = (data: ProductSelectDto) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.product.getProducts(),
    });
    queryClient.setQueryData(queryKeys.product.getProduct(data.id), data);
  };

  return useMutation<ProductSelectDto, Error, ProductUpdateDto & { id: string }>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can update products');
      }

      const { id, ...updateData } = data;
      return await apiPatch<ProductSelectDto>({
        body: updateData,
        path: `/v1/innovator/products/${id}`,
      });
    },
    onSuccess,
  });
};

export const useInnovatorDeleteProduct = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();
  const { isInnovator } = useGetCurrentUser();

  const onSuccess = (data: { id: string }) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.product.getProducts(),
    });
    queryClient.removeQueries({
      queryKey: queryKeys.product.getProduct(data.id),
    });
  };

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      if (!isInnovator) {
        throw new Error('Only innovators can delete products');
      }

      await apiDelete({
        path: `/v1/innovator/products/${id}`,
      });
      return { id };
    },
    onSuccess,
  });
};
